import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "../../lib/api";
import { usePlatformAuth } from "../../context/platformAuth";

type TenantRow = {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended" | "onboarding";
  created_at: string;
};

type PlatformMe = {
  user_id: string;
  role: "manager" | "viewer";
  telegram_chat_id: string | null;
};

export function SystemDashboardPage() {
  const { bootstrap } = usePlatformAuth();
  const qc = useQueryClient();
  const [telegramChatIdInput, setTelegramChatIdInput] = useState("");
  const [profileNotice, setProfileNotice] = useState("");
  const tenants = useQuery({
    queryKey: ["sys-dashboard-tenants"],
    queryFn: () => apiGet<{ items: TenantRow[]; meta: { total: number } }>("/v3/sys/tenants?page=1&size=6"),
  });
  const me = useQuery({
    queryKey: ["sys-auth-me"],
    queryFn: () => apiGet<PlatformMe>("/v3/sys/auth/me"),
  });

  useEffect(() => {
    setTelegramChatIdInput(me.data?.telegram_chat_id ?? "");
  }, [me.data?.telegram_chat_id]);

  const saveTelegramChatId = useMutation({
    mutationFn: (chatId: string | null) => apiPatch<PlatformMe>("/v3/sys/auth/profile", { telegram_chat_id: chatId }),
    onSuccess: async (data) => {
      setProfileNotice(data.telegram_chat_id ? `Telegram chat linked: ${data.telegram_chat_id}` : "Telegram chat ID cleared.");
      await qc.invalidateQueries({ queryKey: ["sys-auth-me"] });
      await qc.invalidateQueries({ queryKey: ["sys-bootstrap"] });
    },
    onError: (err) => {
      setProfileNotice(err instanceof Error ? err.message : "Unable to save Telegram chat ID.");
    },
  });

  const telegramMiniAppBaseUrl = useMemo(() => `${window.location.origin}/tg/system/login`, []);

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">Platform Diagnostics</h1>
      <section className="card-grid">
        <MetricCard title="Total tenants" value={String(bootstrap?.summary.tenants_total ?? 0)} />
        <MetricCard title="Active" value={String(bootstrap?.summary.tenants_active ?? 0)} />
        <MetricCard title="Suspended" value={String(bootstrap?.summary.tenants_suspended ?? 0)} />
        <MetricCard title="Onboarding" value={String(bootstrap?.summary.tenants_onboarding ?? 0)} />
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Telegram Web App Access</h2>
        <p className="text-sm text-slate-600 mt-1">
          Link your Telegram chat ID to enable system manager sign-in via <code>/tg/system/login</code>.
        </p>
        <div className="grid gap-3 mt-3 sm:grid-cols-[1fr_auto]">
          <input
            className="input"
            placeholder="Telegram chat_id (e.g. 123456789)"
            value={telegramChatIdInput}
            onChange={(e) => setTelegramChatIdInput(e.target.value)}
          />
          <button
            className="btn"
            disabled={saveTelegramChatId.isPending}
            onClick={() => {
              setProfileNotice("");
              saveTelegramChatId.mutate(telegramChatIdInput.trim() || null);
            }}
          >
            {saveTelegramChatId.isPending ? "Saving..." : "Save Telegram ID"}
          </button>
        </div>
        <div className="mt-3 grid gap-2 text-xs text-slate-600">
          <div>
            Linked chat ID: <span className="font-mono text-slate-800">{me.data?.telegram_chat_id || "not linked"}</span>
          </div>
          <div>
            Mini App URL: <span className="font-mono break-all text-slate-800">{telegramMiniAppBaseUrl}</span>
          </div>
          <div>
            Optional startapp values: <code>sys_tenants</code>, <code>sys_abuse_dashboard</code>, <code>sys_limits</code>, <code>sys_blocklist</code>, <code>sys_presets</code>.
          </div>
          {profileNotice ? <div className="text-sm text-slate-800">{profileNotice}</div> : null}
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Recent Tenants</h2>
        <div className="grid gap-2 mt-2">
          {(tenants.data?.items ?? []).map((t) => (
            <article key={t.id} className="rounded border border-slate-200 p-3 bg-white">
              <p className="font-semibold">{t.name}</p>
              <p className="text-xs text-slate-600">
                {t.slug} | {t.status}
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <article className="metric-card">
      <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
      <p className="text-3xl font-semibold mt-2">{value}</p>
    </article>
  );
}

